
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

// Export the Career Counselor association name constant as a string
export const CAREER_COUNSELOR_NAME = 'Career Counselor'; // String name for Career Counselor association

// Export the UUID value - initially empty, will be set during initialization
export let CAREER_COUNSELOR_ID = ''; // This will hold the actual UUID from the database

export interface CareerCounselor {
  id: string;
  name: string;
  email: string;
  education?: string;
  experience?: number;
  about_us?: string;
  profile_image?: string;
  min_session_time: number;
  session_charge: number;
  currency: string;
  pricing_id: string; // The actual UUID from partner_pricing table
  association_id?: string; // Optional field to store the association ID
}

// Initialize the CAREER_COUNSELOR_ID as soon as possible
(async () => {
  try {
    const { data, error } = await supabase
      .from('associations')
      .select('id')
      .eq('name', CAREER_COUNSELOR_NAME)
      .single();
    
    if (error) {
      console.error('Error fetching Career Counselor ID:', error);
    } else if (data) {
      CAREER_COUNSELOR_ID = data.id;
      console.log('Career Counselor Association ID initialized:', CAREER_COUNSELOR_ID);
    }
  } catch (error) {
    console.error('Failed to initialize CAREER_COUNSELOR_ID:', error);
  }
})();

export const useCareerCounselors = () => {
  const [counselors, setCounselors] = useState<CareerCounselor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load counselors on mount
  useEffect(() => {
    console.log('useCareerCounselors hook initializing');
    getCareerCounselors().then(data => {
      console.log('Counselors loaded:', data.length);
      setCounselors(data);
    }).catch(err => {
      console.error('Error in useEffect when loading counselors:', err);
    }).finally(() => {
      setLoading(false);
    });
  }, []);

  const getCareerCounselors = async (): Promise<CareerCounselor[]> => {
    try {
      setLoading(true);
      console.log('Fetching career counselors...');
      
      // If CAREER_COUNSELOR_ID is not set yet, fetch it
      if (!CAREER_COUNSELOR_ID) {
        console.log('CAREER_COUNSELOR_ID not set, fetching from database');
        const { data: associations, error: associationsError } = await supabase
          .from('associations')
          .select('id')
          .eq('name', CAREER_COUNSELOR_NAME)
          .single();
        
        if (associationsError) {
          console.error('Error fetching associations:', associationsError);
          throw associationsError;
        }
        
        if (!associations) {
          console.warn('No association found for Career Counselor');
          return [];
        }
        
        // Store the actual UUID for Career Counselor association
        const careerCounselorAssociationId = associations.id;
        
        // Update the exported constant with the actual UUID
        CAREER_COUNSELOR_ID = careerCounselorAssociationId;
        
        console.log('Career Counselor Association ID:', CAREER_COUNSELOR_ID);
      }
      
      // Get all partner associations with career counselor association
      const { data: partnerAssociations, error: paError } = await supabase
        .from('partner_associations')
        .select('partner_id')
        .eq('association_id', CAREER_COUNSELOR_ID);
      
      if (paError) {
        console.error('Error fetching partner associations:', paError);
        throw paError;
      }
      
      // Extract partner IDs to use in the IN clause
      const partnerIds = partnerAssociations.map(pa => pa.partner_id);
      
      if (partnerIds.length === 0) {
        console.log('No career counselors found');
        return [];
      }
      
      console.log(`Found ${partnerIds.length} career counselors, fetching details`);
      
      // Use the array of IDs in the in() clause
      const { data, error } = await supabase
        .from('partners')
        .select(`
          *,
          pricing:partner_pricing(*)
        `)
        .eq('status', 'active')
        .in('id', partnerIds);
      
      if (error) {
        console.error('Error fetching partner details:', error);
        throw error;
      }
      
      console.log(`Successfully fetched ${data.length} career counselor details`);
      
      return data.map((partner: any) => {
        const pricing = partner.pricing.find((p: any) => 
          p.association_id === CAREER_COUNSELOR_ID
        );
        
        return {
          id: partner.id,
          name: partner.name,
          email: partner.email,
          education: partner.education,
          experience: partner.experience,
          about_us: partner.about_us,
          profile_image: partner.profile_image,
          min_session_time: pricing?.min_session_time || 30,
          session_charge: pricing?.session_charge || 0,
          currency: pricing?.currency || 'INR',
          pricing_id: pricing?.id || '', // Include the actual pricing_id from the database
          association_id: CAREER_COUNSELOR_ID // Add the association_id to each counselor
        };
      });
    } catch (error) {
      setError(error.message);
      console.error('Error fetching career counselors:', error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  return {
    counselors,
    getCareerCounselors,
    loading,
    error
  };
};

export default useCareerCounselors;
